import { useEffect, useRef, useState } from 'react'

import { getOfflineEpisode, getViewCache } from '@/lib/db'

type UseViewCacheResult = {
  /** キャッシュ済みBlobURL（offlineEpisodes優先、次にviewCache） */
  cachedBlobUrl: string | null
  isLoading: boolean
}

/**
 * 閲覧キャッシュを読み込むフック
 *
 * チェック順:
 * 1. offlineEpisodes（手動ダウンロード）
 * 2. viewCache（自動キャッシュ）
 * 3. null（キャッシュなし）
 *
 * ObjectURLのライフサイクルを管理し、アンマウント時にrevokeする
 */
export const useViewCache = (episodeId: number | null): UseViewCacheResult => {
  const [cachedBlobUrl, setCachedBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (episodeId === null) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    const check = async () => {
      try {
        // 1. offlineEpisodes（手動DL）を優先チェック
        const offline = await getOfflineEpisode(episodeId)
        if (cancelled) return
        if (offline) {
          const url = URL.createObjectURL(offline.blob)
          objectUrlRef.current = url
          setCachedBlobUrl(url)
          setIsLoading(false)
          return
        }

        // 2. viewCache（自動キャッシュ）
        const cached = await getViewCache(episodeId)
        if (cancelled) return
        if (cached) {
          const url = URL.createObjectURL(cached.blob)
          objectUrlRef.current = url
          setCachedBlobUrl(url)
          setIsLoading(false)
          return
        }

        // 3. キャッシュなし
        setIsLoading(false)
      } catch {
        if (!cancelled) setIsLoading(false)
      }
    }

    check()

    return () => {
      cancelled = true
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [episodeId])

  return { cachedBlobUrl, isLoading }
}
